import { format } from 'date-fns';
import clsx from 'clsx';
import type { PromotionRow } from '@/lib/types';

interface Props {
  rows: PromotionRow[];
  isLoading: boolean;
}

const STATUS_TONES: Record<PromotionRow['status'], string> = {
  active: 'bg-success/15 text-success',
  pending: 'bg-featured/15 text-featured',
  expired: 'bg-text-muted/10 text-text-muted',
  cancelled: 'bg-text-muted/10 text-text-muted',
  refunded: 'bg-danger/15 text-danger',
};

export function PromotionTable({ rows, isLoading }: Props) {
  if (isLoading) {
    return <div className="py-12 text-center text-text-muted">Loading…</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="py-12 text-center text-text-muted">
        No boosts yet. <br />
        <span className="text-sm">Buy your first boost from the Boosts tab.</span>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead className="border-b border-border-subtle text-xs font-semibold uppercase tracking-wide text-text-muted">
          <tr>
            <th className="py-3 pr-4">Slot / Package</th>
            <th className="py-3 pr-4">Window</th>
            <th className="py-3 pr-4">Status</th>
            <th className="py-3 pr-4 text-right">Spend</th>
            <th className="py-3 pr-4 text-right">Impressions</th>
            <th className="py-3 pr-4 text-right">Clicks</th>
            <th className="py-3 pr-4 text-right">CTR</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-border-subtle/60 last:border-0">
              <td className="py-3 pr-4">
                <div className="font-semibold">{r.slot ?? '—'}</div>
                <div className="text-xs text-text-muted">{r.package ?? '—'}</div>
              </td>
              <td className="py-3 pr-4 text-text-secondary">
                {r.starts_at ? format(new Date(r.starts_at), 'MMM d, HH:mm') : '—'}
                <span className="mx-1 text-text-muted">→</span>
                {r.ends_at ? format(new Date(r.ends_at), 'MMM d, HH:mm') : '—'}
              </td>
              <td className="py-3 pr-4">
                <span className={clsx('pill', STATUS_TONES[r.status])}>{r.status}</span>
              </td>
              <td className="py-3 pr-4 text-right">${Number(r.price_paid_cad ?? 0).toFixed(0)}</td>
              <td className="py-3 pr-4 text-right tabular-nums">{Number(r.impressions ?? 0).toLocaleString()}</td>
              <td className="py-3 pr-4 text-right tabular-nums">{Number(r.clicks ?? 0).toLocaleString()}</td>
              <td className="py-3 pr-4 text-right tabular-nums">{(Number(r.ctr ?? 0) * 100).toFixed(2)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
